import DOMPurify, { type Config } from "dompurify";

const SANITIZE_CONFIG: Config = {
	ALLOWED_TAGS: [
		"p",
		"div",
		"span",
		"br",
		"b",
		"strong",
		"i",
		"em",
		"u",
		"s",
		"strike",
		"a",
		"img",
		"ul",
		"ol",
		"li",
		"table",
		"tr",
		"td",
		"th",
		"tbody",
		"thead",
		"tfoot",
		"colgroup",
		"col",
		"h1",
		"h2",
		"h3",
		"h4",
		"h5",
		"h6",
		"blockquote",
		"pre",
		"code",
		"hr",
		"font",
		"small",
		"sup",
		"sub",
	],
	ALLOWED_ATTR: [
		"href",
		"target",
		"rel",
		"src",
		"alt",
		"width",
		"height",
		"border",
		"cellspacing",
		"cellpadding",
		"style",
		"class",
		"align",
		"valign",
		"bgcolor",
		"color",
		"size",
		"face",
		"dir",
		"id",
	],
	ALLOWED_URI_REGEXP:
		/^(?:(?:https?|mailto|tel|cid|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
	FORBID_TAGS: [
		"script",
		"style",
		"iframe",
		"object",
		"embed",
		"meta",
		"link",
		"head",
		"title",
		"base",
		"form",
		"input",
		"button",
		"textarea",
		"select",
	],
	FORBID_ATTR: [
		"onerror",
		"onload",
		"onclick",
		"onfocus",
		"onblur",
		"onmouseover",
		"onmouseout",
		"onkeyup",
		"onkeydown",
		"onkeypress",
		"onchange",
		"onsubmit",
	],
	KEEP_CONTENT: true,
};

function parseDeclarations(body: string): Record<string, string> {
	const out: Record<string, string> = {};
	for (const decl of body.split(";")) {
		const trimmed = decl.trim();
		if (!trimmed) continue;
		const colon = trimmed.indexOf(":");
		if (colon < 0) continue;
		const prop = trimmed.slice(0, colon).trim().toLowerCase();
		const value = trimmed.slice(colon + 1).trim();
		if (prop && value) out[prop] = value;
	}
	return out;
}

function applyDeclsRespectingInline(
	el: HTMLElement,
	props: Record<string, string>,
): void {
	const existing = (el.getAttribute("style") ?? "").trim();
	const existingProps = new Set(Object.keys(parseDeclarations(existing)));
	const additions: string[] = [];
	for (const [prop, value] of Object.entries(props)) {
		if (existingProps.has(prop)) continue;
		additions.push(`${prop}: ${value}`);
	}
	if (additions.length === 0) return;
	const next = existing
		? `${additions.join("; ")}; ${existing}`
		: additions.join("; ");
	el.setAttribute("style", next);
}

function flattenStyleBlocks(doc: Document): void {
	const styles = Array.from(doc.querySelectorAll("style"));
	for (const styleEl of styles) {
		const cssText = styleEl.textContent ?? "";
		const cleaned = cssText.replace(/\/\*[\s\S]*?\*\//g, "");
		// Match top-level rules — selector list followed by `{ ... }`.
		const RULE_RE = /([^{}@]+)\{([^{}]*)\}/g;
		for (
			let match = RULE_RE.exec(cleaned);
			match !== null;
			match = RULE_RE.exec(cleaned)
		) {
			const selectorList = match[1].trim();
			const declarations = parseDeclarations(match[2]);
			if (Object.keys(declarations).length === 0) continue;
			for (const selector of selectorList.split(",")) {
				const sel = selector.trim();
				if (!sel) continue;
				let nodes: NodeListOf<Element>;
				try {
					nodes = doc.querySelectorAll(sel);
				} catch {
					continue;
				}
				for (const node of Array.from(nodes)) {
					applyDeclsRespectingInline(node as HTMLElement, declarations);
				}
			}
		}
		styleEl.remove();
	}
}

function rewriteCidImages(
	doc: Document,
	cidMap: Record<string, { contentType: string; contentBytesB64: string }>,
): void {
	const imgs = Array.from(doc.querySelectorAll("img"));
	for (const img of imgs) {
		const src = img.getAttribute("src") ?? "";
		if (!src.toLowerCase().startsWith("cid:")) continue;
		const cid = src.slice(4);
		const data = cidMap[cid] ?? cidMap[cid.split("@")[0] ?? ""];
		if (!data) continue;
		img.setAttribute(
			"src",
			`data:${data.contentType};base64,${data.contentBytesB64}`,
		);
	}
}

export interface ComposeBodyStyle {
	fontFamily: string;
	fontSize: string;
	/** Auto-detected from signature's <a> tags; styles autolinks in drafted
	 * bodies to match the firm's theme color. */
	linkColor?: string;
}

/** Returns the first explicit `color:` on an `<a>` tag (or styled child span)
 * in doc order — typically the firm's hyperlink theme color. */
export function detectLinkColor(doc: Document): string | null {
	const anchors = Array.from(doc.querySelectorAll("a"));
	for (const a of anchors) {
		const style = a.getAttribute("style") ?? "";
		const m = style.match(/(?:^|\s|;)color\s*:\s*([^;]+)/i);
		if (m) return m[1].trim();
		for (const sp of Array.from(a.querySelectorAll("[style*='color']"))) {
			const ms = (sp.getAttribute("style") ?? "").match(/color\s*:\s*([^;]+)/i);
			if (ms) return ms[1].trim();
		}
	}
	return null;
}

/**
 * Heuristic: the first doc-order element with substantive text (not a
 * URL/email/digit) is usually a signature's sign-off line, styled in the
 * user's compose font. Walks ancestors for inline font info to cover both
 * Outlook-Word (span in MsoNormal) and OWA (styled div) shapes.
 */
export function detectComposeBodyStyle(doc: Document): ComposeBodyStyle | null {
	const root = doc.body ?? doc.documentElement;
	if (!root) return null;
	const TEXT_NODE = 3;
	const stack: Element[] = [root];
	while (stack.length > 0) {
		const el = stack.shift() as Element;
		const direct = Array.from(el.childNodes)
			.filter((n) => n.nodeType === TEXT_NODE)
			.map((n) => n.textContent ?? "")
			.join("")
			.trim();
		if (
			direct.length >= 5 &&
			!/^https?:\/\/\S+$/.test(direct) &&
			!/^\S+@\S+\.\S+$/.test(direct) &&
			!/^[\d\s().+-]+$/.test(direct)
		) {
			let cur: Element | null = el;
			while (cur) {
				const style = cur.getAttribute("style") ?? "";
				const ff = style.match(/font-family\s*:\s*([^;]+)/i);
				const fs = style.match(/font-size\s*:\s*([^;]+)/i);
				if (ff && fs) {
					return {
						fontFamily: ff[1].trim(),
						fontSize: fs[1].trim(),
					};
				}
				cur = cur.parentElement;
			}
		}
		for (const child of Array.from(el.children)) {
			stack.push(child);
		}
	}
	return null;
}

export interface SanitizeOptions {
	parser?: DOMParser;
	purify?: typeof DOMPurify;
	cidMap?: Record<string, { contentType: string; contentBytesB64: string }>;
}

export function sanitizeClipboardHtml(
	rawHtml: string,
	opts: SanitizeOptions = {},
): string {
	const parser =
		opts.parser ?? (typeof DOMParser !== "undefined" ? new DOMParser() : null);
	if (!parser) {
		throw new Error("DOMParser not available; pass opts.parser explicitly.");
	}
	const doc = parser.parseFromString(rawHtml, "text/html");
	flattenStyleBlocks(doc);
	if (opts.cidMap) {
		rewriteCidImages(doc, opts.cidMap);
	}
	const flattened = doc.body?.innerHTML ?? doc.documentElement.innerHTML;
	const purify = opts.purify ?? DOMPurify;
	return purify.sanitize(flattened, SANITIZE_CONFIG) as unknown as string;
}
